import { Action }            from 'redux';
import {
    Messages,
    Message,
    MessagesRange,
    MessageDeliveryStatuses,
    SocketMessages,
    MessageActions,
    Recipient
}                            from '../../types';
import { MessagesError }     from '../../reduxTypes';
import { trackMessage }      from '../../helpers/segment';
import ActionTypes           from '../../ActionTypes';
import socketService         from '../../services/SocketService';
import messageFeedService    from '../../services/MessageFeedService';
import sendMessagesService   from '../../services/SendMessagesService';
import { getSession }        from '../../services/SessionService';
import { createTimestamp }   from '../../helpers/timeUtils';
import { nextConvoLoaded }   from '../NextConversation/NextConversationActions';

const messageListeners = Object.create(null);

export interface MessageLoadedAction extends Action {
    conversationId: number;
    messages: Messages;
    total_count: number;
}

export interface MessageUpdateAction extends Action {
    conversationId: number;
    messages: Messages;
    total_count: number;
}

export interface MessageSendAction extends Action {
    conversationId: number;
    message: Message;
}

export interface MessageSendErrorAction extends Action {
    conversationId: number;
    message: Message;
    error: MessagesError;
}

interface SocketMessageResponse {
    message: Message;
}

function loadMessagesSuccess(messages: Messages, conversationId: number, total_count: number): MessageLoadedAction {
    return {
        type: ActionTypes.MESSAGES_LOADED,
        messages,
        conversationId,
        total_count
    };
}

function updateMessagesSuccess(messages: Messages, conversationId: number, total_count?: number): MessageUpdateAction {
    return {
        type: ActionTypes.MESSAGES_UPDATE,
        messages,
        conversationId,
        total_count
    };
}

function loadMessagesError(error: TypeError) {
    return {
        type: ActionTypes.MESSAGES_LOAD_ERROR,
        error
    };
}

function sendMessageRequest(message: Message, conversationId: number): MessageSendAction {
    return {
        type: ActionTypes.MESSAGES_SEND_REQUEST,
        message,
        conversationId,
    };
}

function sendMessageSuccess(message: Message, conversationId: number): MessageSendAction {
    return {
        type: ActionTypes.MESSAGES_SEND_SUCCESS,
        message,
        conversationId
    };
}

function sendMessageError(message: Message, conversationId: number, error): MessageSendErrorAction {
    return {
        type: ActionTypes.MESSAGES_SEND_ERROR,
        message,
        conversationId,
        error
    };
}

export function createMessagePayload(messageBody: string, templateId?: string) {
    return {
        action: MessageActions.SEND,
        content: messageBody,
        date_added: createTimestamp(),
        delivery_status: MessageDeliveryStatuses.IN_PROGRESS,
        id: Math.random().toString(36).substr(2, 10),
        currentSession: true,
        templateId
    };
}

export function loadMessages(conversationId: number, hasNextConvoState?: boolean) {
    return async function loadMessagesAction(dispatch) {
        try {
            const messagePayload = await messageFeedService(conversationId, { before: 0 });

            dispatch(loadMessagesSuccess(messagePayload.details.messages, conversationId, messagePayload.details.total_count));

            if (hasNextConvoState) {
                dispatch(nextConvoLoaded());
            }
        } catch (error) {
            dispatch(loadMessagesError(error));
        }
    };
}

export function sendMessage(conversationId: number, messageBody: string, activeLanguage: string, recipient?: Recipient, templateId?: string) {
    return async function dispatchMessage(dispatch) {
        const messagePayload = createMessagePayload(messageBody, templateId);
        dispatch(sendMessageRequest(messagePayload, conversationId));

        try {
            const session = await getSession();
            const { details: { message }} = await sendMessagesService(session, conversationId, messagePayload, activeLanguage, recipient);
            trackMessage();
            dispatch(sendMessageSuccess(message, conversationId));
        } catch (error) {
            dispatch(sendMessageError(messagePayload, conversationId, error));
        }
    };
}

export function updateMessages(conversationId: number, range: MessagesRange) {
    return async function updateMessagesAction(dispatch) {
        const { details: { messages, total_count }} = await messageFeedService(conversationId, range);

        dispatch(updateMessagesSuccess(messages, conversationId, total_count));
    };
}

export function listenMessages(conversationId: number) {
    return async function messageUpdateListener(dispatch) {
        messageListeners[conversationId] = function onMessageUpdate(response) {
            const { message }: SocketMessageResponse = response;
            // Ignore messages from other conversations
            if (message.conversation_id !== conversationId) {
                return;
            }

            dispatch(updateMessagesSuccess([message], conversationId));
        };
        socketService.on(SocketMessages.UPDATE, messageListeners[conversationId]);
    };
}

export function unlistenMessages(conversationId: number) {
    return function messageUpdateListener() {
        socketService.off(SocketMessages.UPDATE, messageListeners[conversationId]);
        messageListeners[conversationId] = null;
    };
}



// WEBPACK FOOTER //
// ./src/sections/MessagesFeed/MessagesActions.ts